import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Building2, Trash2, Eye, Search, Users, Power } from "lucide-react";
import { CountryDisplay } from "@/components/ui/country-combobox";
import { formatSectorLabel } from "@/lib/sector-labels";

interface Workspace {
  id: string;
  companyName: string;
  sector: string | null;
  country: string | null;
  status: "active" | "disabled";
  createdAt: string;
  memberCount?: number;
  assessmentCount?: number;
}

interface WorkspaceMember {
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
  role: string;
}

interface WorkspaceDetail {
  workspace: Workspace;
  members: WorkspaceMember[];
}

async function fetchWorkspaces(): Promise<{ data: Workspace[]; total: number }> {
  const response = await fetch("/api/admin/workspaces?pageSize=100", { credentials: "include" });
  if (!response.ok) throw new Error("Failed to fetch workspaces");
  return response.json();
}

async function fetchWorkspaceDetail(id: string): Promise<WorkspaceDetail> {
  const response = await fetch(`/api/admin/workspaces/${id}`, { credentials: "include" });
  if (!response.ok) throw new Error("Failed to fetch workspace");
  return response.json();
}

async function deleteWorkspace(id: string) {
  const response = await fetch(`/api/admin/workspaces/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to delete workspace");
  }
  return response.json();
}

async function toggleWorkspaceStatus(id: string, status: "active" | "disabled") {
  const response = await fetch(`/api/admin/workspaces/${id}/toggle-status`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ status }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to toggle workspace status");
  }
  return response.json();
}

export default function AdminWorkspaces() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [workspaceDetail, setWorkspaceDetail] = useState<WorkspaceDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["/api/admin/workspaces"],
    queryFn: fetchWorkspaces,
    enabled: user?.role === "admin",
  });

  const deleteMutation = useMutation({
    mutationFn: deleteWorkspace,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/workspaces"] });
      toast({ title: "Workspace deleted", description: "The workspace has been removed." });
      setDeleteDialogOpen(false);
      setSelectedWorkspaceId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Deletion failed", description: error.message, variant: "destructive" });
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "active" | "disabled" }) =>
      toggleWorkspaceStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/workspaces"] });
      toast({
        title: "Workspace updated",
        description: variables.status === "active" ? "Workspace enabled successfully." : "Workspace disabled successfully.",
      });
    },
    onError: (error: Error) => {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    },
  });

  const handleViewDetail = async (id: string) => {
    setLoadingDetail(true);
    setDetailDialogOpen(true);
    try {
      const detail = await fetchWorkspaceDetail(id);
      setWorkspaceDetail(detail);
    } catch {
      toast({ title: "Error", description: "Failed to load workspace details", variant: "destructive" });
      setDetailDialogOpen(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="max-w-md bg-card rounded-3xl shadow-sm outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
          <div className="px-6 py-5">
            <h3 className="text-xl font-bold text-foreground">{"Access Denied"}</h3>
            <p className="text-sm text-slate-500 mt-1">{"You must be a platform administrator to view this page."}</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const workspaces = data?.data || [];
  const filtered = workspaces.filter(
    (w: Workspace) => !search || w.companyName.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">
          {"Workspace Management"}
        </h1>
        <p className="text-slate-500 mt-2">
          {"View and manage all company workspaces"}
        </p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={"Search by company name..."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-slate-50 rounded-2xl"
        />
      </div>

      <div className="bg-card rounded-3xl shadow-[0px_4px_6px_-4px_rgba(226,232,240,0.50),0px_10px_15px_-3px_rgba(226,232,240,0.50)] outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
        <div className="px-6 py-5 border-b border-border/30">
          <div className="flex items-center gap-3">
            <div className="w-[43px] h-[43px] bg-primary/10 rounded-2xl flex items-center justify-center">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground">
              {`Workspaces (${filtered.length})`}
            </h3>
          </div>
        </div>
        <div className="p-6">
          {filtered.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{"No workspaces found"}</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Company Name"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Sector"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Country"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Status"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Members"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Assessments"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Created"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide text-right">{"Actions"}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((w) => (
                    <TableRow key={w.id} className="border-t border-slate-100">
                      <TableCell className="font-semibold text-slate-900">{w.companyName}</TableCell>
                      <TableCell>
                        {w.sector ? (
                          <span className="px-2.5 py-1 bg-primary/10 rounded-full text-primary text-xs font-bold capitalize">
                            {formatSectorLabel(w.sector)}
                          </span>
                        ) : "-"}
                      </TableCell>
                      <TableCell className="text-slate-700"><CountryDisplay value={w.country} /></TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
                          w.status === "active"
                            ? "bg-emerald-50 text-emerald-600"
                            : "bg-slate-100 text-slate-600"
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            w.status === "active" ? "bg-emerald-500" : "bg-slate-400"
                          }`} />
                          {w.status === "active" ? "Active" : "Disabled"}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-700">{w.memberCount ?? "-"}</TableCell>
                      <TableCell className="text-slate-700">{w.assessmentCount ?? "-"}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {w.createdAt ? formatDate(w.createdAt) : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-xl"
                            onClick={() => handleViewDetail(w.id)}
                            title={"View details"}
                          >
                            <Eye className="h-4 w-4 text-primary" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-xl"
                            onClick={() =>
                              toggleStatusMutation.mutate({
                                id: w.id,
                                status: w.status === "active" ? "disabled" : "active",
                              })
                            }
                            title={w.status === "active" ? "Disable workspace" : "Enable workspace"}
                            disabled={toggleStatusMutation.isPending}
                          >
                            <Power className={`h-4 w-4 ${w.status === "active" ? "text-amber-500" : "text-emerald-600"}`} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-xl text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setSelectedWorkspaceId(w.id);
                              setDeleteDialogOpen(true);
                            }}
                            title={"Delete workspace"}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-slate-900">{"Delete Workspace"}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              {"Are you sure you want to delete this workspace? This will permanently remove all associated data including assessments, members, and reports. This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">{"Cancel"}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedWorkspaceId && deleteMutation.mutate(selectedWorkspaceId)}
              className="bg-red-600 hover:bg-red-700 rounded-2xl"
            >
              {"Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-[500px] rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">{"Workspace Details"}</DialogTitle>
          </DialogHeader>
          {loadingDetail ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : workspaceDetail ? (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-2xl space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{"Company:"}</span>
                  <span className="font-semibold text-slate-900">{workspaceDetail.workspace.companyName}</span>
                </div>
                {workspaceDetail.workspace.sector && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{"Sector:"}</span>
                    <span className="font-semibold text-slate-900 capitalize">{formatSectorLabel(workspaceDetail.workspace.sector)}</span>
                  </div>
                )}
                {workspaceDetail.workspace.country && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{"Country:"}</span>
                    <CountryDisplay value={workspaceDetail.workspace.country} className="font-semibold text-slate-900" />
                  </div>
                )}
              </div>
              <div>
                <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  {`Members (${workspaceDetail.members.length})`}
                </h4>
                {workspaceDetail.members.length === 0 ? (
                  <p className="text-sm text-slate-500">{"No members"}</p>
                ) : (
                  <div className="space-y-2">
                    {workspaceDetail.members.map((m) => (
                      <div key={m.user.id} className="flex items-center justify-between text-sm p-2.5 bg-slate-50 rounded-xl">
                        <span className="text-slate-700">
                          {m.user.firstName && m.user.lastName ? `${m.user.firstName} ${m.user.lastName}` : m.user.email}
                        </span>
                        <span className="px-2.5 py-1 bg-primary/10 rounded-full text-primary text-xs font-bold capitalize">
                          {m.role.replace("_", " ")}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
